import React from "react";
import { ChatThread } from "@src/features/aiAssist/uiComponents/ChatThread";
import { ChatComposer } from "@src/features/aiAssist/uiComponents/ChatComposer";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import { ChatThreadProps } from "../ChatThread";
import { ChatComposerProps } from "../ChatComposer";
import { ChatSidebarDragOverlay } from "@src/features/assistantChat/ChatSidebarDragOverlay";
import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/18px/x";

import { ChatHistoryProps } from "../ChatHistory";
import { Tooltip, TooltipTrigger, TooltipContent } from "@src/lib/ui/Tooltip";
import { Button, ButtonProps } from "@src/lib/ui/Button";
import { useAiChatComposerFileUpload } from "@src/features/aiAssist/hooks/useAiChatComposerFileUpload";
import { ChatHistoryIconButton } from "../ChatHistoryIconButton";
import Compose2Icon from "@src/lib/icons/18px/compose-2";
import AskOpusOrbIcon from "@src/features/assistantChat/AskOpusOrbIcon";

export interface ChatSidebarProps {
  headerProps: ChatHeaderProps;
  threadProps: ChatThreadProps;
  composerProps: ChatComposerProps;
  disabledMessage?: { title: string; description: string };
  className?: string;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  threadProps,
  composerProps,
  className = "",
  headerProps,
  disabledMessage,
}) => {
  const enableDragAndDrop = composerProps.attachmentProps?.enableAttachments;
  const { dropzoneProps } = useAiChatComposerFileUpload({
    onAttachmentsChanged: composerProps.attachmentProps?.onAttachmentsChanged,
    enableDragAndDrop,
  });

  if (disabledMessage) {
    return (
      <div
        className={cn(
          "flex h-full flex-col items-center justify-center relative p-8",
          className,
        )}
      >
        <HeaderIconButton
          variant="secondary"
          tooltip="Close"
          className="absolute top-2 right-2"
          onClick={headerProps.onClickClose}
        >
          <XIcon className="size-3 text-gray-500" />
        </HeaderIconButton>
        <div className="flex flex-col items-center justify-center gap-2">
          <AskOpusOrbIcon size={48} state="idle" still className="mb-4" />
          <Text variant="compact-title" multiline>
            {disabledMessage.title}
          </Text>
          <Text
            variant="compact-title"
            className="text-muted-foreground text-center"
            multiline
          >
            {disabledMessage.description}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("relative flex h-full min-w-0 flex-col pb-2", className)}
      {...(dropzoneProps?.getRootProps() || {})}
    >
      {headerProps && <ChatHeader {...headerProps} />}
      <div className="flex h-full min-w-0 flex-col px-2">
        <ScrollableFlexGrow
          className="min-w-0 flex-1 overflow-hidden"
          innerClassName="min-w-0 px-2"
          innerStyle={{ overflowX: "hidden", overflowY: "auto" }}
        >
          <ChatThread {...threadProps} />
        </ScrollableFlexGrow>
        <ChatComposer {...composerProps} />
      </div>
      {enableDragAndDrop && dropzoneProps && (
        <ChatSidebarDragOverlay isDragActive={dropzoneProps.isDragActive} />
      )}
    </div>
  );
};

export default ChatSidebar;

export type ChatHeaderProps = {
  title?: string;
  onClickNewChat: () => void;
  onClickClose: () => void;
  chatHistoryProps?: ChatHistoryProps;
};

const ChatHeader: React.FC<ChatHeaderProps> = ({
  title,
  onClickNewChat,
  onClickClose,
  chatHistoryProps,
}) => {
  return (
    <div className="flex min-h-12 items-center justify-between border-b border-gray-2 px-[12px]">
      <Text variant="compact-title">{title || "Ask Opus"}</Text>
      <div className="flex items-center gap-1.5">
        {onClickNewChat && (
          <HeaderIconButton onClick={onClickNewChat} tooltip="Start new chat">
            <Compose2Icon className="size-3.5" />
          </HeaderIconButton>
        )}
        {chatHistoryProps && <ChatHistoryIconButton {...chatHistoryProps} />}
        <HeaderIconButton
          variant="secondary"
          onClick={onClickClose}
          tooltip="Close"
          className="ml-1"
        >
          <XIcon className="size-3 text-gray-500" />
        </HeaderIconButton>
      </div>
    </div>
  );
};

interface HeaderIconButtonProps {
  onClick?: () => void;
  tooltip: string;
  children: React.ReactNode;
  variant?: ButtonProps["variant"];
  className?: string;
}

const HeaderIconButton: React.FC<HeaderIconButtonProps> = ({
  onClick,
  tooltip,
  children,
  variant = "ghost",
  className = "",
}) => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger asChild>
      <Button
        onClick={onClick}
        variant={variant}
        size="icon-xs"
        className={cn(className)}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{tooltip}</TooltipContent>
  </Tooltip>
);
